import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react'
import { createPortal } from 'react-dom'
import { useUiState } from '@/core/state/react'
import { uiActions } from '@/core/state/ui'
import { appActions } from '@/core/state/app'
import { setQuickFilterBarVisible } from '@/core/commands/filters'
import { MENU_SECTIONS, anyMatches, visibleItems, type MenuItem } from './menuItems'
import './menu.css'

/**
 * The navigation drawer (Phase 1E), replacing `components/menu/MenuPanel.vue`.
 *
 * It renders from the frame rather than the island and portals to <body>, as
 * Vuetify teleported it, so no ancestor can clip or restack it. Its open state
 * is `uiStore.modals.menuPanel` — the same flag the Vue toolbar's Menu button
 * already sets through `uiActions.openMenuPanel()`, which is why a Vue toolbar
 * can open a React drawer with no bridge.
 *
 * Every action goes straight to a `uiActions` member or a Phase 1D command.
 */

const CLOSE_AFTER_CONFIG_MS = 100

/**
 * What each item does. The Vue version reached these through 27 emitted events.
 *
 * EVERY action item closes the drawer — Network & Config after 100ms so the
 * modal opens first, the rest immediately. The visual baselines are what proved
 * it: the V05 modal screenshots capture the whole viewport, and a drawer left
 * open behind the dialog moved 27,000 pixels.
 */
function runItem(slug: string, feature: string | undefined, close: () => void) {
  // Before the switch, so every branch records it — including the ones that
  // return early. Items with no feature clear nothing: `guided-tour` leaves the
  // previous highlight alone, which is what the Vue drawer did.
  if (feature) uiActions.setActiveMenuFeature(feature)
  switch (slug) {
    // Data
    case 'import-data':
      uiActions.openDataModal()
      return close()
    case 'load-mock-network':
      uiActions.openDataModal('mock')
      return close()
    case 'clean-data':
      uiActions.openDataCleaning()
      return close()
    // Design
    case 'optimize-network':
      uiActions.openOptimizationConfig()
      return close()
    case 'optimize-across-futures':
      uiActions.openConfigModal('simulation', 'uncertainty')
      return close()
    case 'network-config':
      // The one item that waits before closing, so the modal opens first.
      uiActions.openConfigModal()
      window.setTimeout(close, CLOSE_AFTER_CONFIG_MS)
      return
    case 'add-node':
      uiActions.openAddNodeModal()
      return close()
    // Analyze
    case 'compare-scenarios':
      uiActions.openScenariosModal()
      return close()
    case 'run-simulation':
      uiActions.openSimulationConfig()
      return close()
    case 'purchase-method':
      uiActions.openPurchaseMethodAnalysis()
      return close()
    case 'negotiation':
      uiActions.openNegotiation()
      return close()
    // Report
    case 'powerpoint':
      uiActions.openPPTExport()
      return close()
    case 'excel':
      uiActions.openReporting()
      return close()
    case 'presentation-mode':
      uiActions.openCustomizeMapOnPresentation()
      return close()
    // View & Tools
    case 'customize-map':
      uiActions.openCustomizeMap()
      return close()
    case 'guided-tour':
      uiActions.startGuidedTour()
      return close()
    default:
      return
  }
}

function Switch({
  checked,
  inset,
  label,
  onChange,
  tabIndex,
  onFocus,
}: {
  checked: boolean
  inset: boolean
  label: string
  onChange: (next: boolean) => void
  /** A switch row's roving stop is this control, not the row around it. */
  tabIndex?: number
  onFocus?: () => void
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      tabIndex={tabIndex}
      onFocus={onFocus}
      className={`menu-switch${inset ? ' menu-switch--inset' : ''}`}
      onClick={(event) => {
        event.stopPropagation()
        onChange(!checked)
      }}
    >
      <span className="menu-switch__track">
        <span className="menu-switch__thumb" />
      </span>
    </button>
  )
}

/**
 * The drawer's body. It is a separate component so that each opening mounts a
 * fresh one and the search query resets on its own: a menu reopened is a menu
 * unfiltered. `MenuPanel` keys it by opening count rather than leaving that to
 * the unmount, which the exit animation delays. Resetting the query from an
 * effect instead would be a synchronous setState inside an effect, which the
 * React Compiler rules reject and which costs a second render for nothing.
 *
 * This is a deliberate change, not parity. The Vue drawer's `searchQuery` was a
 * ref in a component that stayed mounted with the drawer merely toggled, and
 * nothing reset it — so typing "excel", closing, and reopening gave you a menu
 * still filtered to one item, with no memory of having filtered it.
 */
function MenuDrawer({ closing }: { closing: boolean }) {
  const barVisible = useUiState((s) => s.quickFilterBar)
  const aiChatOpen = useUiState((s) => s.modals.aiChat)
  const activeFeature = useUiState((s) => s.activeMenuFeature)
  const [query, setQuery] = useState('')

  const close = () => uiActions.closeModal('menuPanel')

  /**
   * Escape closes it, and focus goes back where it came from.
   *
   * Neither is a regression — Vuetify's `VNavigationDrawer` has no keydown
   * handler and no focus management of its own, so the drawer never did either.
   * But this one covers the page behind a scrim and takes the click, so a
   * keyboard user who opens it has no way out and, once it closes, is returned
   * to the top of the document. Both are cheap here and belong with the
   * component rather than with whoever opens it.
   *
   * `document.activeElement` is read at mount, which is the Menu button in
   * every path that exists today. Restoring is guarded on the element still
   * being in the document: an item that replaces the toolbar would otherwise
   * throw on an unmounted node.
   */
  /**
   * Which row carries the list's single tab stop.
   *
   * A roving tabindex: the list is ONE stop in the page's Tab order, as
   * Vuetify's `v-list` made it, and the arrow keys move within it. Without it
   * Tab visited all eighteen rows one at a time before reaching anything past
   * the drawer.
   */
  const [rovingIndex, setRovingIndex] = useState(0)
  const opener = useRef<Element | null>(null)
  const panel = useRef<HTMLElement | null>(null)
  useEffect(() => {
    opener.current = document.activeElement
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.stopPropagation()
      close()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      const previous = opener.current
      if (previous instanceof HTMLElement && previous.isConnected) previous.focus()
    }
    // Mount and unmount only: the listener must not be torn down and re-added
    // on every keystroke in the search field.
  }, [])

  /**
   * Every place the keyboard can land inside the drawer, in document order.
   *
   * Read from the DOM rather than tracked in state because the list is
   * filtered by the search field: an index into "the items" stops meaning
   * anything the moment a keystroke removes half of them.
   */
  const stops = (): HTMLElement[] => {
    const root = panel.current
    if (!root) return []
    // No visibility filter: a row the search has filtered out is not in the
    // DOM at all, so there is nothing hidden to skip. (`offsetParent` would
    // have been the obvious guard and is always null under jsdom, which makes
    // it both unnecessary and untestable.)
    return Array.from(
      root.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      ),
    )
  }

  /** The rows the arrow keys move between — the list, not the header or search. */
  const rowStops = (): HTMLElement[] =>
    stops().filter((el) => el.closest('.menu-list-row') !== null)

  /**
   * Tab stays inside, and the arrow keys walk the list.
   *
   * Vuetify's `v-list` gave both for free: it bound ArrowDown/Up/Home/End and
   * made the whole menu ONE tab stop. Rebuilt in React as plain buttons, the
   * drawer had neither — eighteen tab stops, and Tab walked straight out of an
   * open drawer into the page behind it, which a sighted mouse user never sees
   * and a keyboard user cannot avoid.
   */
  const onPanelKeyDown = (event: ReactKeyboardEvent<HTMLElement>) => {
    if (event.key === 'Tab') {
      const all = stops()
      if (all.length === 0) return
      const first = all[0]
      const last = all[all.length - 1]
      const active = document.activeElement
      // Wrap at both ends. Without this the next Tab leaves for the page
      // underneath, which is still there and still clickable.
      if (event.shiftKey && (active === first || !panel.current?.contains(active))) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      }
      return
    }

    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return
    const rows = rowStops()
    if (rows.length === 0) return
    // Let the search field keep its own caret keys; only Home/End would
    // collide, and inside a text box those belong to the text.
    if (event.target instanceof HTMLInputElement && (event.key === 'Home' || event.key === 'End')) return

    event.preventDefault()
    const current = rows.indexOf(document.activeElement as HTMLElement)
    const next =
      event.key === 'Home' ? 0
        : event.key === 'End' ? rows.length - 1
          // From outside the list (the search field), ArrowDown enters at the
          // top rather than jumping to the second item.
          : current === -1 ? (event.key === 'ArrowDown' ? 0 : rows.length - 1)
            : (current + (event.key === 'ArrowDown' ? 1 : -1) + rows.length) % rows.length
    rows[next]?.focus()
    setRovingIndex(next)
  }

  const anythingMatches = anyMatches(query)

  /**
   * Both switches announce themselves — "shown"/"hidden", the Vue wording — and
   * close the drawer only when turning ON, to save a click.
   */
  const toggle = (label: string, next: boolean, apply: (on: boolean) => void) => {
    apply(next)
    appActions.showSnackbar(`${label} ${next ? 'shown' : 'hidden'}`, 'info')
    if (next) close()
  }

  /**
   * Every visible row, in render order, so a row knows its own number without
   * anything being mutated while React renders. The order matches `rowStops()`
   * exactly — one focusable control per row — which is what lets the roving
   * index and the arrow keys mean the same thing.
   */
  const visibleOrder = MENU_SECTIONS.flatMap((section) => visibleItems(section.name, query))

  const renderItem = (item: MenuItem) => {
    const rowIndex = visibleOrder.indexOf(item)
    const titleClass =
      item.emphasis === 'killer' ? 'feature-title' : item.emphasis === 'special' ? 'special-title' : 'compact-title'
    const subtitleClass = item.emphasis === 'killer' ? 'feature-subtitle' : 'compact-subtitle'
    const isActive = Boolean(item.feature) && item.feature === activeFeature

    /**
     * A switch row is a <div>, not a <button>.
     *
     * Its control is the inner `role="switch"` button, and a button inside a
     * button is invalid HTML — React says so, browsers reparent it, and a
     * screen reader is handed two nested controls where there is one. The row
     * has no click of its own either way: `onClick` below is undefined for it.
     * Every button default this class needs is reset explicitly (border,
     * background, font, text-align, width, display), so the two render alike.
     */
    const Row = item.kind === 'action' ? 'button' : 'div'

    return (
      <div role="listitem" className="menu-list-row" key={item.slug}>
        <Row
          type={item.kind === 'action' ? 'button' : undefined}
          data-testid={`menu-item-${item.slug}`}
          data-tour={item.tour ?? undefined}
          className={
            'menu-list-item' +
            (item.emphasis === 'special' ? ' menu-list-item--special' : '') +
            (isActive ? ' menu-list-item--active' : '')
          }
          aria-current={isActive ? 'true' : undefined}
          // The roving stop. A switch row's own control is the inner button,
          // so the row itself is never tabbable and its switch takes the turn.
          tabIndex={item.kind === 'action' ? (rowIndex === rovingIndex ? 0 : -1) : undefined}
          onFocus={item.kind === 'action' ? () => setRovingIndex(rowIndex) : undefined}
          onClick={item.kind === 'action' ? () => runItem(item.slug, item.feature, close) : undefined}
        >
          <span className="icon-wrapper">
            <i className={`mdi ${item.icon} menu-icon`} aria-hidden="true" />
          </span>
          <span className="menu-item-body">
            <span className={titleClass}>{item.title}</span>
            {item.subtitle && <span className={subtitleClass}>{item.subtitle}</span>}
          </span>
          {item.slug === 'filter-bar' && (
            <Switch
              checked={barVisible}
              inset
              label="Filter Bar"
              tabIndex={rowIndex === rovingIndex ? 0 : -1}
              onFocus={() => setRovingIndex(rowIndex)}
              onChange={(next) => toggle('Quick Filter Bar', next, setQuickFilterBarVisible)}
            />
          )}
          {item.slug === 'ai-chat' && (
            <Switch
              checked={aiChatOpen}
              inset={false}
              label="AI Chat"
              tabIndex={rowIndex === rovingIndex ? 0 : -1}
              onFocus={() => setRovingIndex(rowIndex)}
              onChange={(next) =>
                toggle('AI Chat', next, (on) => (on ? uiActions.openAIChat() : uiActions.closeAIChat()))
              }
            />
          )}
        </Row>
      </div>
    )
  }

  return createPortal(
    <>
      <div
        className={`scvc-menu-scrim${closing ? ' is-closing' : ''}`}
        onClick={close}
        data-testid="menu-scrim"
      />
      {/*
        A <nav>, not an <aside>. Vuetify's drawer defaulted to `tag: 'nav'`, so
        it was a navigation landmark; `<aside>` maps to `complementary`, and a
        screen-reader user cycling landmarks for the app's navigation skipped
        straight past the app's navigation. It keeps the accessible name the
        Vuetify one never had.

        The section wrappers carry `role="list"` and each row a `role="listitem"`
        for the same reason: `v-list` announced "list, 17 items" and gave
        position information, and bare buttons in bare divs announce neither.
      */}
      <nav
        ref={panel}
        onKeyDown={onPanelKeyDown}
        className={`scvc-menu-panel${closing ? ' is-closing' : ''}`}
        data-testid="menu-panel"
        aria-label="Network Optimizer menu"
      >
        <div className="menu-header">
          <div className="menu-header-content">
            <div className="menu-header-brand">
              <i className="mdi mdi-network menu-header-icon" aria-hidden="true" />
              <div>
                <div className="menu-title">Network Optimizer</div>
                <div className="menu-subtitle">Design, optimize, analyze</div>
              </div>
            </div>
            <button type="button" className="close-btn" onClick={close} aria-label="Close menu">
              <i className="mdi mdi-close" aria-hidden="true" />
            </button>
          </div>
        </div>

        <div className="menu-search-container">
          <div className="menu-search-field" data-testid="menu-search">
            <i className="mdi mdi-magnify" aria-hidden="true" />
            <input
              type="text"
              placeholder="Search menu..."
              value={query}
              aria-label="Search menu"
              onChange={(event) => setQuery(event.target.value)}
            />
            {query && (
              <button type="button" className="menu-search-clear" aria-label="Clear search" onClick={() => setQuery('')}>
                <i className="mdi mdi-close-circle" aria-hidden="true" />
              </button>
            )}
          </div>
        </div>

        <div className="feature-list">
          {query && !anythingMatches && (
            <div className="empty-search-state">
              <i className="mdi mdi-magnify empty-search-icon" aria-hidden="true" />
              <div className="empty-search-text">No results found</div>
              <div className="empty-search-subtext">Try a different search term</div>
            </div>
          )}

          {MENU_SECTIONS.map((section) => (
            <div key={section.name} role="list" aria-label={section.name}>
              {/* Gated on whether ANYTHING matches, not on this section — the
                  Vue version kept every header up during a search that found
                  anything at all. */}
              {(!query || anythingMatches) && (
                <div className="section-header-subtle-compact">
                  {section.step && <span className="step-num">{section.step}</span>}
                  {section.name}
                </div>
              )}
              {visibleItems(section.name, query).map(renderItem)}
            </div>
          ))}
        </div>
      </nav>
    </>,
    document.body,
  )
}

/**
 * How long the drawer stays mounted after the store closes it.
 *
 * It matches the 0.2s `scvc-menu-out` / `scvc-scrim-out` animations in
 * menu.css. React has no unmount hook a stylesheet can hang off, so the exit
 * has to be held open here — without it the panel and its scrim vanished in a
 * single frame, where Vuetify's drawer slid out over the same 200ms.
 *
 * Someone with `prefers-reduced-motion: reduce` gets no animation, and this
 * delay is then 200ms of an already-invisible drawer sitting in the DOM: it
 * takes no clicks (`pointer-events: none`) and shows nothing.
 */
const EXIT_MS = 200

export function MenuPanel() {
  const open = useUiState((s) => s.modals.menuPanel)
  const [closing, setClosing] = useState(false)
  const [wasOpen, setWasOpen] = useState(open)
  // A fresh instance per opening, so the search query resets on the way in
  // rather than on the way out. It used to rely on the unmount, which the exit
  // hold above delays by 200ms — reopening inside that window kept the old
  // query and left the menu filtered.
  const [openings, setOpenings] = useState(0)

  // Adjusted during render rather than from an effect: this is state derived
  // from a change in another value, which React documents as a render-time
  // adjustment, and `react-hooks/set-state-in-effect` rightly rejects the
  // effect version. The re-render happens before anything is painted.
  if (open !== wasOpen) {
    setWasOpen(open)
    setClosing(!open)
    if (open) setOpenings((n) => n + 1)
  }

  useEffect(() => {
    if (!closing) return
    const timer = window.setTimeout(() => setClosing(false), EXIT_MS)
    return () => window.clearTimeout(timer)
  }, [closing])

  if (!open && !closing) return null
  return <MenuDrawer key={openings} closing={!open} />
}
